import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import ExternalReviewPackageCard from '../components/externalReview/ExternalReviewPackageCard';
import ResponsibleUseBoundary from '../components/externalReview/ResponsibleUseBoundary';
import {
  mapExternalReviewPackageList,
  normalizeSafetyCaseListResponse,
} from '../domain/externalReview/mapExternalReviewPackageList';
import { reportsApi } from '../services/reportsApi';

export default function ExternalSafetyReviewHome() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<ReturnType<typeof mapExternalReviewPackageList>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await reportsApi.listSafetyCases({ limit: 50 });
        if (cancelled) return;
        const cases = normalizeSafetyCaseListResponse(response);
        setPackages(mapExternalReviewPackageList(cases));
      } catch {
        if (!cancelled) {
          setError('Unable to load assigned Safety Review packages.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={700}>
          Safety Review Packages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Assigned Signed Safety Scan packages available for external review. Open a package to review
          workplace conditions, evidence, corrective-action status, and claim boundaries.
        </Typography>
      </Stack>

      <ResponsibleUseBoundary />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!error && packages.length === 0 ? (
        <Alert severity="info" data-testid="external-review-empty-state">
          No finalized Safety Review packages are assigned to your account yet. Your Edgentik host will
          share a direct review link when a Signed Safety Scan is ready.
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {packages.map((item) => (
          <ExternalReviewPackageCard key={item.reportId} item={item} />
        ))}
      </Stack>
    </Stack>
  );
}
